import { getTranslation } from 'helpers/getTranslation';

import { SURVEY_TYPE_CONFIGS, SurveyTypeConfig } from './SurveyItem.config';

type SurveyAnswer = {
  key: string;
  value: string;
};

type Survey = {
  type: string;
  answers: SurveyAnswer[];
};

export type PreparedSurveyAnswer = {
  question: string;
  answer: string;
};

const KEY_LIKE_VALUE_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const buildAnswersMap = (answers: SurveyAnswer[]): Record<string, string> =>
  Object.fromEntries(answers.map(({ key, value }) => [key, value]));

const resolveAnswerTranslationKey = (
  fieldKey: string,
  value: string,
  fieldConfig: SurveyTypeConfig['fields'][number],
  surveyConfig: SurveyTypeConfig,
): string | null => {
  const answerGroups = [
    fieldConfig.answerI18nGroup,
    ...(fieldConfig.sharedAnswerI18nGroups ?? []),
  ].filter(Boolean) as string[];

  for (const answerGroup of answerGroups) {
    const translationKey = surveyConfig.answerI18nKeys[answerGroup]?.[value];

    if (translationKey) {
      return translationKey;
    }
  }

  for (const answerGroup of Object.values(surveyConfig.answerI18nKeys)) {
    if (answerGroup[value]) {
      return answerGroup[value];
    }
  }

  return null;
};

const formatAnswerValue = (
  fieldKey: string,
  value: string | undefined,
  fieldConfig: SurveyTypeConfig['fields'][number],
  surveyConfig: SurveyTypeConfig,
): string => {
  if (!value) {
    return '-';
  }

  if (fieldConfig.isFreeText) {
    return value;
  }

  const translationKey = resolveAnswerTranslationKey(
    fieldKey,
    value,
    fieldConfig,
    surveyConfig,
  );

  if (translationKey) {
    const translatedValue = getTranslation(translationKey);

    if (translatedValue !== translationKey) {
      return translatedValue;
    }
  }

  if (KEY_LIKE_VALUE_PATTERN.test(value)) {
    const fallbackTranslationKey = `survey_item.fallback_answers.${value.replace(/-/g, '_')}`;
    const fallbackTranslation = getTranslation(fallbackTranslationKey);

    if (fallbackTranslation !== fallbackTranslationKey) {
      return fallbackTranslation;
    }
  }

  return value;
};

export const prepareSurveyAnswers = (survey: Survey): PreparedSurveyAnswer[] => {
  const surveyConfig = SURVEY_TYPE_CONFIGS[survey.type];
  const answersMap = buildAnswersMap(survey.answers);

  if (!surveyConfig) {
    return survey.answers.map(({ key, value }) => ({
      question: key,
      answer: value || '-',
    }));
  }

  return surveyConfig.fields.reduce<PreparedSurveyAnswer[]>(
    (preparedAnswers, fieldConfig) => {
      if (!(fieldConfig.fieldKey in answersMap)) {
        return preparedAnswers;
      }

      if (fieldConfig.isHidden?.(answersMap)) {
        return preparedAnswers;
      }

      preparedAnswers.push({
        question: getTranslation(fieldConfig.questionI18nKey),
        answer: formatAnswerValue(
          fieldConfig.fieldKey,
          answersMap[fieldConfig.fieldKey],
          fieldConfig,
          surveyConfig,
        ),
      });

      return preparedAnswers;
    },
    [],
  );
};
